import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { UserIdService } from '../../../core/auth';
import { SearchAction } from '../../../core/features/store/actions/search.actions';
import { AssignedShipTo, AvailableShipTosSearchParams, Pagination } from '../../../core/model';
import { AvailableShipTosFacade } from '../facades';
import { SoldToActions } from '../store/actions';
import { SoldToSelectors } from '../store/selectors/index';
import { AVAILABLE_SHIP_TOS, AvailableShipTosSearchParamsState, StateWithSoldTo } from '../store/sold-to-state';

@Injectable()
export class AvailableShipTosService implements AvailableShipTosFacade {
  constructor(private store: Store<StateWithSoldTo>, private userIdService: UserIdService) {}

  searchForAvailableShipTos(searchParams: AvailableShipTosSearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new SoldToActions.LoadAvailableShipTos(searchParams, userId)),
      () => {}
    );
  }

  getAvailableShipTos(): Observable<Array<AssignedShipTo>> {
    return this.store
      .select(SoldToSelectors.getAvailableShipTosCombinedSearchPageResultsIdsSelector)
      .pipe(switchMap((results) => this.store.select(SoldToSelectors.getAvailableShipTosSearchResultEntries, { keys: results })));
  }

  getAvailableShipTosLoading(): Observable<boolean> {
    return this.store
      .select(SoldToSelectors.getAvailableShipTosSearchPageResultsSelector)
      .pipe(map((loaderState) => loaderState.loading));
  }

  getAvailableShipTosPagination(): Observable<Pagination | undefined> {
    return this.store.select(SoldToSelectors.getAvailableShipTosSearchPaginationSelector);
  }

  getSearchParametersForAvailableShipTos(
    key: string = 'latest',
    userUid?: string
  ): Observable<AvailableShipTosSearchParamsState> {
    return this.store.select(SoldToSelectors.getAvailableShipTosSearchParamsSelector).pipe(
      map((params) => params as AvailableShipTosSearchParams),
      tap((params: AvailableShipTosSearchParams) => {
        if (!params || !params.key) {
          const initialSearchParams: AvailableShipTosSearchParams = {
            key: key,
            page: 0,
            count: 10,
          };

          if (userUid) {
            initialSearchParams.userUid = userUid;
          }

          this.updateSearchParametersForAvailableShipTos(initialSearchParams);
        }
      }),
      filter((params) => params && !!params.key)
    );
  }

  updateSearchParametersForAvailableShipTos(searchParams: AvailableShipTosSearchParams) {
    this.store.dispatch(new SearchAction(searchParams, AVAILABLE_SHIP_TOS));
  }
}
